/**
 * StepToken — Step 1 of onboarding.
 * Lets the user generate an API token for MCP access.
 * The full token is shown ONCE immediately after creation.
 */

import { useState } from 'react';
import { createApiToken } from '../../api/client';
import type { ApiTokenCreated } from '../../types';

// ---------------------------------------------------------------------------
// CopyButton — inline copy with "Copied!" feedback
// ---------------------------------------------------------------------------

interface CopyButtonProps {
  text: string;
  label: string;
  className?: string;
}

function CopyButton({ text, label, className = '' }: CopyButtonProps) {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label={label}
      className={`shrink-0 px-3 py-2 text-xs font-medium rounded-lg transition-colors ${
        copied
          ? 'bg-green-700/40 text-green-300 border border-green-700/60'
          : 'bg-gray-700 hover:bg-gray-600 border border-gray-600 text-gray-200'
      } ${className}`}
    >
      {copied ? 'Copied!' : 'Copy'}
    </button>
  );
}

// ---------------------------------------------------------------------------
// NewTokenDisplay — shown after generation
// ---------------------------------------------------------------------------

interface NewTokenDisplayProps {
  token: ApiTokenCreated;
}

function NewTokenDisplay({ token }: NewTokenDisplayProps) {
  return (
    <div className="space-y-4">
      {/* Warning */}
      <div className="rounded-lg border border-amber-700/60 bg-amber-900/20 px-4 py-3">
        <p className="text-sm font-semibold text-amber-300">
          This token is shown only once. Save it now.
        </p>
      </div>

      {/* Token value */}
      <div>
        <p className="text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wide">API Token</p>
        <div className="flex items-start gap-2">
          <code className="flex-1 bg-gray-900/60 border border-gray-700 rounded-lg px-3 py-2.5 text-sm text-green-300 font-mono break-all select-all">
            {token.token}
          </code>
          <CopyButton text={token.token} label="Copy token" />
        </div>
        <p className="text-xs text-gray-500 mt-1.5">
          You'll plug this into your agent's MCP config on the next step.
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface StepTokenProps {
  token: ApiTokenCreated | null;
  hasExistingTokens: boolean;
  onTokenGenerated: (token: ApiTokenCreated) => void;
  onNext: () => void;
  onBack: () => void;
  nextEnabled: boolean;
}

export function StepToken({
  token,
  hasExistingTokens,
  onTokenGenerated,
  onNext,
  onBack,
  nextEnabled,
}: StepTokenProps) {
  const [tokenName, setTokenName] = useState('MCP Client');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleGenerate() {
    if (isGenerating) return;
    setIsGenerating(true);
    setError(null);
    try {
      const created = await createApiToken({ name: tokenName.trim() || 'MCP Client' });
      onTokenGenerated(created);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate token');
    } finally {
      setIsGenerating(false);
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display font-bold text-2xl text-white">Generate your MCP API token</h2>
        <p className="text-gray-400 mt-1 text-sm">
          This token authorizes your MCP client to access your sharpelab account.
          You'll plug it into Claude Code, Codex, or Cursor in the next step.
        </p>
      </div>

      {token ? (
        <NewTokenDisplay token={token} />
      ) : hasExistingTokens ? (
        <div className="space-y-4">
          {/* Existing token notice */}
          <div className="rounded-lg border border-primary-700/60 bg-primary-900/20 px-4 py-3 space-y-1">
            <p className="text-sm font-semibold text-primary-200">
              You already have an API token.
            </p>
            <p className="text-xs text-gray-300 leading-relaxed">
              Use the one you saved earlier — you can also revoke or rotate it
              anytime under Settings &rarr; API Tokens. Continue to the next
              step to wire it into your agent.
            </p>
          </div>

          {error && (
            <div className="rounded-lg border border-red-700/60 bg-red-900/20 px-3 py-2">
              <p className="text-sm text-red-300">{error}</p>
            </div>
          )}

          {/* Optional regen */}
          <details className="group">
            <summary className="text-xs text-gray-500 hover:text-gray-300 cursor-pointer select-none transition-colors">
              Lost it? Generate a replacement token instead.
            </summary>
            <div className="mt-3 space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wide">
                  Token name
                </label>
                <input
                  type="text"
                  value={tokenName}
                  onChange={(e) => setTokenName(e.target.value)}
                  placeholder="MCP Client"
                  className="w-full bg-gray-800/60 border border-gray-600 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent placeholder-gray-500"
                />
              </div>
              <button
                type="button"
                onClick={handleGenerate}
                disabled={isGenerating}
                className="w-full py-2.5 rounded-lg border border-gray-600 text-gray-200 hover:bg-gray-700/40 transition-colors text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isGenerating ? 'Generating...' : 'Generate a new token'}
              </button>
              <p className="text-xs text-gray-500">
                The previous token stays valid until you revoke it in Settings.
              </p>
            </div>
          </details>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Token name input */}
          <div>
            <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wide">
              Token name
            </label>
            <input
              type="text"
              value={tokenName}
              onChange={(e) => setTokenName(e.target.value)}
              placeholder="MCP Client"
              className="w-full bg-gray-800/60 border border-gray-600 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent placeholder-gray-500"
            />
            <p className="text-xs text-gray-500 mt-1">Optional — name to identify this token later.</p>
          </div>

          {error && (
            <div className="rounded-lg border border-red-700/60 bg-red-900/20 px-3 py-2">
              <p className="text-sm text-red-300">{error}</p>
            </div>
          )}

          {/* Generate button */}
          <button
            type="button"
            onClick={handleGenerate}
            disabled={isGenerating}
            className="btn-glow w-full py-3 rounded-xl text-white font-semibold text-sm disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isGenerating ? 'Generating...' : 'Generate Token'}
          </button>
        </div>
      )}

      {/* Navigation */}
      <div className="flex items-center gap-3 pt-2">
        <button
          type="button"
          onClick={onBack}
          className="px-5 py-2.5 rounded-lg border border-gray-700 text-gray-300 hover:text-white hover:border-gray-500 transition-colors text-sm font-medium"
        >
          Back
        </button>
        <div className="flex-1" />
        <button
          type="button"
          onClick={onNext}
          disabled={!nextEnabled}
          className="px-6 py-2.5 rounded-lg bg-primary-600 hover:bg-primary-500 disabled:bg-gray-700 disabled:cursor-not-allowed text-white font-medium text-sm transition-colors"
        >
          Next
        </button>
      </div>
    </div>
  );
}
